import type { ResolvedMonospaceCollection } from '#layers/data-model/public';
import type { CollectionCodeOperation } from './types';
import { camelCase } from 'scule';
import { formatJavascriptMemberExpression } from '~/utils/code-snippet/js-serialization/strings';
import { ONE_ITEM_OPERATIONS } from './operation';

/**
 * Derives the SDK result variable name from collection and operation names.
 */
export function getDefaultResultVariableName(collection: ResolvedMonospaceCollection, operation: CollectionCodeOperation): string {
	const collectionName = camelCase(collection.apiName);
	const fallback = ONE_ITEM_OPERATIONS.has(operation) ? 'item' : 'items';
	const variableName = ONE_ITEM_OPERATIONS.has(operation) ? singularizeIdentifier(collectionName) : collectionName;

	return toSafeIdentifier(variableName, fallback);
}

/**
 * Applies a small English singularization pass for generated variable names.
 */
function singularizeIdentifier(value: string): string {
	if (value.endsWith('ies') && value.length > 3) {
		return `${value.slice(0, -3)}y`;
	}

	if (value.endsWith('s') && !value.endsWith('ss') && value.length > 1) {
		return value.slice(0, -1);
	}

	return value;
}

/**
 * Removes invalid identifier characters and falls back when nothing safe remains.
 */
function toSafeIdentifier(value: string, fallback: string): string {
	const sanitized = value.replace(/^[^a-z_$]+/i, '').replaceAll(/[^\w$]/g, '');

	return isSafeJavascriptIdentifier(sanitized) ? sanitized : fallback;
}

/**
 * Checks whether a value can be emitted as a JavaScript identifier.
 */
function isSafeJavascriptIdentifier(value: string): boolean {
	return formatJavascriptMemberExpression('__target__', value) === `__target__.${value}`;
}
